"use client";

import React from "react";
import DiagramSection from "@/components/diagrams/DiagramSection";
import DiagramCard from "@/components/diagrams/DiagramCard";
import WedgeWireProfileDiagram from "@/components/diagrams/WedgeWireProfileDiagram";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface CopyText {
  title: string;
  lead: string;
  sideTitle: string;
  sideParagraph1: string;
  sideParagraph2: string;
  cardTitle: string;
  cardDesc: string;
}
const copyData: Record<Lang, CopyText> = {
  en: {
    title: "V-Wire Profile Geometry & Support Architecture",
    lead: "Continuous resistance welding fuses profiled V-wires to rigid transverse support rods, producing smooth inward-widening slots engineered for continuous solid-liquid separation.",
    sideTitle: "Continuous Slot Geometry & Two-Point Particle Contact",
    sideParagraph1: "Each profile wire presents a flat top working surface that tapers downward into a sharp V-shaped wedge. Particles contacting the surface can bridge only across two points at the slot opening, preventing mid-depth wedging and blinding.",
    sideParagraph2: "Because the slot expands progressively downstream, any solid particle smaller than the slot aperture releases immediately into the effluent flow. The underlying transverse support rods provide rigid resistance against mechanical torque and heavy differential pressure.",
    cardTitle: "Wedge Wire Cross-Section Profile",
    cardDesc: "Profile wire cross-section showing continuous slot opening, resistance weld nugget to support rod, and downward widening relief angle."
  },
  zh: {
    title: "V 型型线几何原理与支撑架构",
    lead: "连续电阻熔焊将高精度 V 型截面丝与横向支撑筋条牢固结合，形成内宽外窄的连续缝隙，专为连续固液分离与介质截留而设计。",
    sideTitle: "连续自清理缝隙与双点接触防堵机制",
    sideParagraph1: "每根型线表面平整作为工作截面，向下呈锐角梯形收窄。截留颗粒仅在网面缝隙处形成双点线接触，彻底消除深层颗粒嵌塞与网孔堵塞现象。",
    sideParagraph2: "缝隙沿流向呈放射状逐渐扩大，通过缝隙的微粒立即随流体释出。底部刚性横向支撑筋条与型线交叉点全熔焊连接，赋予滤网极高承压与抗形变刚度。",
    cardTitle: "V 型楔形丝截面几何解析",
    cardDesc: "型线截面原理展示连续缝隙微孔、支撑筋条电阻熔焊焊核与向内自释放防堵通道。"
  },
  es: {
    title: "Geometría del perfil de alambre en V y arquitectura de soporte",
    lead: "La soldadura por resistencia continua une alambres perfilados en V a varillas de soporte transversales, creando ranuras continuas con alivio interior diseñadas para la separación sólido-líquido.",
    sideTitle: "Geometría de ranura continua y contacto de dos puntos",
    sideParagraph1: "Cada alambre perfilado presenta una superficie de trabajo plana que se estrecha en cuña en V hacia el interior. Las partículas en la superficie hacen contacto solo en dos puntos, evitando la obstrucción y el colmatado.",
    sideParagraph2: "Como la ranura se ensancha progresivamente aguas abajo, cualquier partícula que cruce la abertura se libera de inmediato en el efluente. Las varillas de soporte transversales proporcionan rigidez ante presiones diferenciales elevadas.",
    cardTitle: "Perfil transversal de alambre en cuña",
    cardDesc: "Corte transversal que muestra la abertura de ranura continua, la soldadura a la varilla de soporte y el ángulo de alivio interior antiobstrucción."
  },
  de: {
    title: "V-Profildraht-Geometrie & Tragstabarchitektur",
    lead: "Kontinuierliche Widerstandsschweißung verbindet profilierte V-Drähte mit Querstäben zu kontinuierlichen Spalten mit selbstreinigendem Freiwinkel für die Fest-Flüssig-Trennung.",
    sideTitle: "Kontinuierliche Spaltgeometrie & Zweipunkt-Partikelkontakt",
    sideParagraph1: "Jeder Profildraht besitzt eine flache Arbeitsseite, die sich nach unten keilförmig verjüngt. Feststoffpartikel berühren die Spaltöffnung nur an zwei Punkten, wodurch Verkeilen und Zusetzen wirksam verhindert werden.",
    sideParagraph2: "Da sich der Spalt in Fließrichtung stetig erweitert, werden Partikel unterhalb der Spaltweite sofort vom Filtrat mitgeführt. Die quer verlaufenden Tragstäbe garantieren hohe mechanische Belastbarkeit bei hohen Differenzdrücken.",
    cardTitle: "Spaltsiebdraht-Profilquerschnitt",
    cardDesc: "Querschnittsdarstellung mit kontinuierlicher Spaltöffnung, Widerstandsschweißverbindung zum Tragstab und nach innen erweiterndem Freiwinkel."
  },
  fr: {
    title: "Géométrie des fils en V et architecture de support",
    lead: "Le soudage par résistance continu fusionne des fils profilés en V sur des tiges de support transversales, formant des fentes continues à évasement intérieur pour la séparation liquide-solide.",
    sideTitle: "Géométrie à fente continue et contact particulaire en deux points",
    sideParagraph1: "Chaque fil profilé offre une surface de travail plane qui s'effile en V vers l'intérieur. Les particules n'ont qu'un contact en deux points sur l'arête de la fente, éliminant tout risque d'encrassement ou de blocage.",
    sideParagraph2: "La fente s'élargissant vers l'aval, toute particule franchissant l'arête est immédiatement évacuée dans l'effluent. Les tiges de support transversales confèrent une rigidité élevée face aux fortes pressions différentielles.",
    cardTitle: "Profil en coupe de fil triangulaire",
    cardDesc: "Coupe transversale montrant l'ouverture de fente continue, le point de soudure sur la tige de support et l'angle de dépouille anti-colmatage."
  },
  pt: {
    title: "Geometria do perfil de arame em V e estrutura de suporte",
    lead: "A eletrossolda por resistência contínua une arames perfilados em V a barras de suporte transversais, criando aberturas contínuas com alívio interno projetadas para separação sólido-líquido.",
    sideTitle: "Geometria de abertura contínua e contato de dois pontos",
    sideParagraph1: "Cada arame perfilado apresenta superfície de trabalho plana que se afunila em V para o interior. Partículas em contato com a superfície tocam apenas dois pontos, impedindo travamento e entupimento do elemento.",
    sideParagraph2: "Com o alargamento progressivo da fenda na direção do fluxo, qualquer partícula menor que a abertura é prontamente liberada. As barras transversais de suporte conferem elevada rigidez sob pressão diferencial severa.",
    cardTitle: "Perfil transversal de tela em cunha",
    cardDesc: "Corte transversal exibindo a abertura contínua da fenda, o ponto de solda na barra de suporte e o ângulo de alívio interno antientupimento."
  }
};
export default function StructureDiagram() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  return <DiagramSection id="wedge-wire-structure" title={c.title} lead={c.lead} tone="muted">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:items-center">
        {/* Descriptive column */}
        <div className="md:col-span-6 space-y-4">
          <h3 className="mf-h3 text-foreground font-default">
            {c.sideTitle}
          </h3>
          <p className="text-base leading-relaxed text-muted-foreground">
            {c.sideParagraph1}
          </p>
          <p className="text-base leading-relaxed text-muted-foreground">
            {c.sideParagraph2}
          </p>
        </div>

        {/* Diagram Card */}
        <div className="md:col-span-6">
          <DiagramCard index="WWS-01" badge="Continuous Resistance Weld" title={c.cardTitle} description={c.cardDesc}>
            <WedgeWireProfileDiagram variant="profile" />
          </DiagramCard>
        </div>
      </div>
    </DiagramSection>;
}